/**
 * 빌드 화면 위 문제 은행 진입 카드 — 누르면 문제 은행을 연다. 순수 UI 다.
 * 이 퀴즈 과목이 문제 은행 노출 과목일 때만 호출부가 그린다. AI 생성 진입 카드와 같은 껍데기(BuildEntryCard)를 쓰지만
 * AI 사용 가능 여부와는 무관하다.
 * Figma `.AI 생성/선택 카드` 문제 은행.
 */
import { Green600 } from '@zep-us/zus/tokens';
import { CheckSmallCircleFillIcon } from '@zep-us/zus-icons';
import { useTranslation } from 'next-i18next';
import { cn } from '@/utils/tailwind.util';
import { BUILD_ENTRY_PICTURE_HOVER_CLASS, BuildEntryCard, type BuildEntryCardSize } from '../../../components/BuildEntryCard';
type QuizBankPreviewSize = BuildEntryCardSize | 'mobile';

/**
 * 앞 카드 폭·보이는 높이(PRD 3-1) — 뒤 2장은 16·32 좁게 6px씩 위로. 높이는 보이는 높이 + 4:
 * 그림을 카드 바닥 아래로 4 더 내려 두어 hover 로 4 올라가도 아래 끝이 바닥 밖에서 잘린다.
 */
const BANK_PREVIEW_SIZE: Record<QuizBankPreviewSize, string> = {
  small: 'w-[200px] h-[70px]',
  large: 'w-[260px] h-[88px]',
  mobile: 'w-[200px] h-[64px]'
};

/** 미리보기 글자 자리 — 한 가지 옅은 회색(gray-200)으로만 그린다(10/23 지민 — 진하거나 색이 섞이면 안 된다). */
const SKELETON_COLOR_CLASS = 'bg-gray-200';
const SkeletonBar = ({
  className
}: {
  className: string;
}) => <span className={cn('block shrink-0 rounded-full', SKELETON_COLOR_CLASS, className)} data-sentry-component="SkeletonBar" data-sentry-source-file="QuizBankEntryCard.tsx" />;

/**
 * 뒤에 겹친 카드 3장 — 앞 카드와 같은 흰 카드를 8px 씩 위로 띄워 쌓고, 뒤로 갈수록 흐리게 한다. 맨 뒤부터 순서대로.
 * 색은 장마다 바꾸지 않고, 테두리만 앞 카드와 같은 진하기로 그려 겹친 게 보이게 한다(10/23 지민 — 회색으로 칠하면 너무 튄다).
 */
const BANK_STACK_LAYERS = ['w-[calc(100%-48px)] opacity-50', 'w-[calc(100%-32px)] opacity-75', 'w-[calc(100%-16px)]'] as const;

/** 미리보기 글자 막대 높이 — 모든 줄을 한 높이로 맞춘다(10/23 지민 — 높이가 제각각이면 글자로 안 읽힌다). */
const SKELETON_BAR_HEIGHT_CLASS = 'h-[6px]';

/**
 * 문제 은행 카드 미리보기 — 문제 카드를 줄여 4장 겹친 그림. 글자 대신 회색 막대로 모양만 보인다
 * (10/23 지민 — 「문제 1 · 선택형」 글자가 작아 지저분해 보이고, 예시 문항은 번역할 수도 없다).
 * 앞 카드는 아주 단순하게 — 초록 체크 + 짧은 막대 / 문제 글 막대 두 줄(10/23 지민, 비교 3안 중 A안).
 */
const QuizBankPreviewPicture = ({
  size
}: {
  size: QuizBankPreviewSize;
}) => <div aria-hidden className={cn('absolute bottom-[-4px] right-lg flex flex-col items-center', BUILD_ENTRY_PICTURE_HOVER_CLASS)} data-sentry-component="QuizBankPreviewPicture" data-sentry-source-file="QuizBankEntryCard.tsx">
    {BANK_STACK_LAYERS.map(layerClass => <div key={layerClass} className={cn('h-[8px] rounded-t-sm border border-b-0 border-opacity-black-3 bg-default', layerClass)} />)}
    <div className={cn('flex flex-col gap-[10px] overflow-hidden rounded-t-sm border border-b-0 border-opacity-black-3 bg-default px-sm pt-sm shadow-sm', BANK_PREVIEW_SIZE[size])}>
      <div className="flex items-center gap-xs">
        <CheckSmallCircleFillIcon size="xs" fill={Green600} aria-hidden data-sentry-element="CheckSmallCircleFillIcon" data-sentry-source-file="QuizBankEntryCard.tsx" />
        <SkeletonBar className={cn(SKELETON_BAR_HEIGHT_CLASS, 'w-[36px]')} data-sentry-element="SkeletonBar" data-sentry-source-file="QuizBankEntryCard.tsx" />
      </div>
      <SkeletonBar className={cn(SKELETON_BAR_HEIGHT_CLASS, 'w-[88%]')} data-sentry-element="SkeletonBar" data-sentry-source-file="QuizBankEntryCard.tsx" />
      <SkeletonBar className={cn(SKELETON_BAR_HEIGHT_CLASS, 'w-[62%]')} data-sentry-element="SkeletonBar" data-sentry-source-file="QuizBankEntryCard.tsx" />
    </div>
  </div>;
export const QuizBankEntryCard = ({
  size,
  onOpen,
  className
}: {
  /** 빈 퀴즈면 크게 — AI 생성 진입 카드와 같은 크기를 쓴다. */
  size: BuildEntryCardSize;
  onOpen: () => void;
  className?: string;
}) => {
  const {
    t
  } = useTranslation(['build']);
  return <BuildEntryCard testId="quiz-bank-entry-card" title={t('build:quizBank.entry.title')} size={size} className={className} onClick={onOpen} data-sentry-element="BuildEntryCard" data-sentry-component="QuizBankEntryCard" data-sentry-source-file="QuizBankEntryCard.tsx">
      <span className="tablet-sm:hidden">
        <QuizBankPreviewPicture size="mobile" data-sentry-element="QuizBankPreviewPicture" data-sentry-source-file="QuizBankEntryCard.tsx" />
      </span>
      <span className="hidden tablet-sm:block">
        <QuizBankPreviewPicture size={size} data-sentry-element="QuizBankPreviewPicture" data-sentry-source-file="QuizBankEntryCard.tsx" />
      </span>
    </BuildEntryCard>;
};